import { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PhotoSlideshow = ({ photos, className = "", autoPlay = true, interval = 4000 }) => {
  const [current, setCurrent] = useState(0);
  const total = photos.length;

  const prev = useCallback(() => setCurrent(c => (c - 1 + total) % total), [total]);
  const next = useCallback(() => setCurrent(c => (c + 1) % total), [total]);

  // Auto-advance
  useEffect(() => {
    if (!autoPlay) return;
    const timer = setInterval(next, interval);
    return () => clearInterval(timer);
  }, [autoPlay, interval, next]);

  if (!photos || total === 0) return null;

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-[#6B3A1F]/10 shadow-lg ${className}`} data-testid="photo-slideshow">
      {/* Main Image - contain to show full photo */}
      <div className="relative bg-[#1a1a1a]" style={{ aspectRatio: '4/3' }}>
        <img
          src={photos[current].src}
          alt={photos[current].alt}
          className="w-full h-full object-cover object-center"
          loading={current === 0 ? "eager" : "lazy"}
        />

        {/* Navigation Arrows */}
        <button
          onClick={prev}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition-colors duration-200"
          data-testid="slideshow-prev"
          aria-label="Previous photo"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <button
          onClick={next}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition-colors duration-200"
          data-testid="slideshow-next"
          aria-label="Next photo"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      {/* Dot indicators */}
      <div className="flex items-center justify-center gap-1.5 py-3 bg-white">
        {photos.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2 h-2 rounded-full transition-colors duration-200 ${i === current ? 'bg-[#D97904] w-4' : 'bg-[#6B3A1F]/20 hover:bg-[#6B3A1F]/40'}`}
            aria-label={`Go to photo ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
